import React, { useCallback, useEffect, useMemo, useState } from "react"
import { IconX } from "@tabler/icons-react"
import { trackAmenClick } from "../../lib/analytics"
import LandingDeclarationScripture from "./LandingDeclarationScripture"

const MEDITATION_SECONDS = 30

type Props = {
  open: boolean
  onClose: () => void
  highlight: string
  fullDeclaration: string
  scripture?: string
  scriptureReference?: string
}

type AscensionParticle = {
  id: number
  left: string
  delay: string
  size: string
  symbol: string
}

const ASCENSION_PARTICLES: AscensionParticle[] = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${8 + (i * 5) % 84}%`,
  delay: `${i * 0.12}s`,
  size: i % 3 === 0 ? "text-lg" : i % 3 === 1 ? "text-sm" : "text-xs",
  symbol: ["✦", "✧", "♱", "✦", "·"][i % 5],
}))

const PrayerAscensionOverlay: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3800)
    return () => clearTimeout(timer)
  }, [onComplete])

  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-2xl">
      <div className="absolute bottom-16 left-1/2 h-64 w-24 -translate-x-1/2 animate-prayer-light-beam bg-gradient-to-t from-[#9e7e43]/60 via-[#fdf6e8]/40 to-transparent" />

      <div className="absolute bottom-20 left-1/2 size-32 -translate-x-1/2 animate-prayer-glow-pulse rounded-full bg-[radial-gradient(circle,rgba(158,126,67,0.5)_0%,transparent_70%)]" />

      {ASCENSION_PARTICLES.map((particle) => (
        <span
          key={particle.id}
          className={`absolute bottom-16 animate-prayer-ascend font-light text-[#9e7e43] ${particle.size}`}
          style={{
            left: particle.left,
            animationDelay: particle.delay,
          }}
        >
          {particle.symbol}
        </span>
      ))}

      <div className="absolute inset-x-0 top-1/3 flex flex-col items-center px-6 text-center">
        <p className="animate-prayer-message-fade font-outfit text-2xl font-medium text-[#2d1a45] md:text-3xl">
          Amen
        </p>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#2d1a45]/10 via-transparent to-[#9e7e43]/5" />
    </div>
  )
}

const LandingPrayerDeclarationModal: React.FC<Props> = ({
  open,
  onClose,
  highlight,
  fullDeclaration,
  scripture,
  scriptureReference,
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(MEDITATION_SECONDS)
  const [amenReady, setAmenReady] = useState(false)
  const [showAscension, setShowAscension] = useState(false)

  const meditationProgress = useMemo(
    () => ((MEDITATION_SECONDS - secondsRemaining) / MEDITATION_SECONDS) * 100,
    [secondsRemaining]
  )

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleAscensionComplete = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!open) return

    setSecondsRemaining(MEDITATION_SECONDS)
    setAmenReady(false)
    setShowAscension(false)

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          setAmenReady(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }

    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      clearInterval(interval)
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [open, handleClose])

  const handleAmen = () => {
    if (!amenReady || showAscension) return
    setShowAscension(true)
    void trackAmenClick("weekly-declaration")
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="weekly-declaration-title"
      onClick={showAscension ? undefined : handleClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        {showAscension && <PrayerAscensionOverlay onComplete={handleAscensionComplete} />}

        {!showAscension && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 flex size-9 items-center justify-center rounded-full text-[#4a454f] transition-colors hover:bg-[#f5f5f5]"
          >
            <IconX size={20} stroke={1.5} />
          </button>
        )}

        <div className="flex flex-col gap-6 px-6 py-8 md:px-10 md:py-10">
          <header className="flex flex-col gap-3 pr-8">
            <span className="font-dm-sans text-xs font-bold uppercase tracking-[1.2px] text-[#9e7e43]">
              Weekly Declaration
            </span>
            <h2
              id="weekly-declaration-title"
              className="font-outfit text-2xl font-medium leading-tight tracking-tight text-[#2d1a45] md:text-3xl md:leading-[1.2]"
            >
              Today, I walk in the{" "}
              <em className="font-medium not-italic text-[#9e7e43]">{highlight}</em>
              {" "}and light of the Almighty.
            </h2>
            <LandingDeclarationScripture
              scripture={scripture}
              scriptureReference={scriptureReference}
            />
          </header>

          <div className="font-dm-sans text-base leading-7 text-[#4a454f] md:text-lg md:leading-8">
            {fullDeclaration.split("\n").map((paragraph, i) => (
              <p key={i} className={i > 0 ? "mt-4" : undefined}>
                {paragraph}
              </p>
            ))}
          </div>

          <div className="flex flex-col gap-3 pt-2">
            {!amenReady && (
              <div className="flex flex-col gap-2">
                <p className="font-dm-sans text-sm text-[#4a454f]">
                  Take a moment to meditate on these words…
                </p>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#f0ebe3]">
                  <div
                    className="h-full rounded-full bg-[#9e7e43] transition-all duration-1000 ease-linear"
                    style={{ width: `${meditationProgress}%` }}
                  />
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleAmen}
              disabled={!amenReady || showAscension}
              className={`inline-flex items-center justify-center rounded-xl px-8 py-3 font-dm-sans text-sm font-semibold tracking-wide transition-all ${
                amenReady && !showAscension
                  ? "bg-[#2d1a45] text-[#fdfdfd] hover:opacity-90 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1)]"
                  : "cursor-not-allowed bg-[#e8e4ec] text-[#9a94a0]"
              }`}
            >
              {showAscension ? "Sending…" : "Amen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LandingPrayerDeclarationModal
